import {
  normalizeChildrensBookManuscriptImportPreview,
  normalizeChildrensBookManuscriptImportPreviewResponse,
} from "./childrensBookManuscriptImportPreviewContract";

import {
  MANUSCRIPT_CONTENT_VERSION,
  normalizeManuscriptContent,
  plainTextFromManuscriptContent,
} from "./childrensBookManuscriptContent";

import {
  prepareTextbookRichManuscriptBlock,
} from "./textbookRichManuscriptBridge";


function clean(
  value
) {
  return String(
    value ?? ""
  ).trim();
}


function safeObject(
  value
) {
  return (
    value &&
    typeof value ===
      "object" &&
    !Array.isArray(
      value
    )
  )
    ? value
    : null;
}


function defaultIdFactory(
  kind,
  index
) {
  return [
    "textbook-import",
    clean(kind) ||
      "item",
    Number(index || 0) + 1,
    Date.now()
      .toString(36),
    Math.random()
      .toString(36)
      .slice(2, 8),
  ].join("-");
}


function normalizedPreview(
  value
) {
  const source =
    safeObject(value);

  if (!source) {
    throw new Error(
      "The Word manuscript preview is invalid."
    );
  }

  if (
    safeObject(
      source.preview
    )
  ) {
    return normalizeChildrensBookManuscriptImportPreviewResponse(
      source
    ).preview;
  }

  return normalizeChildrensBookManuscriptImportPreview(
    source
  );
}


function normalizedContent(
  value,
  location
) {
  const result =
    normalizeManuscriptContent(
      value
    );

  if (
    !result.ok ||
    !result.content
  ) {
    throw new Error(
      `The Word manuscript contains formatting that could not be preserved safely in ${location}.`
    );
  }

  return result.content;
}


function rekeyedContent(
  contents,
  prefix
) {
  const blocks = [];

  (
    Array.isArray(contents)
      ? contents
      : []
  ).forEach(
    (
      content,
      contentIndex
    ) => {
      const normalized =
        normalizedContent(
          content,
          `imported content ${contentIndex + 1}`
        );

      normalized.blocks.forEach(
        (
          block,
          blockIndex
        ) => {
          blocks.push({
            ...block,

            id: [
              prefix,
              contentIndex + 1,
              blockIndex + 1,
            ].join("-"),
          });
        }
      );
    }
  );

  return normalizedContent(
    {
      version:
        MANUSCRIPT_CONTENT_VERSION,

      blocks,
    },
    "the combined Textbook manuscript"
  );
}


function stripStructuralHeading(
  content,
  prefix
) {
  const normalized =
    normalizedContent(
      content,
      "an imported Word section"
    );

  const blocks =
    normalized.blocks.map(
      (block) => ({
        ...block,
      })
    );

  while (
    blocks.length &&
    blocks[0]?.type ===
      "page_break"
  ) {
    blocks.shift();
  }

  if (
    blocks[0]?.type ===
      "heading"
  ) {
    blocks.shift();
  }

  return rekeyedContent(
    [
      {
        version:
          MANUSCRIPT_CONTENT_VERSION,

        blocks,
      },
    ],
    prefix
  );
}


function contentHasMeaningfulText(
  content
) {
  return Boolean(
    clean(
      plainTextFromManuscriptContent(
        content
      )
        .replace(
          /\f/g,
          " "
        )
    )
  );
}


function pushContent(
  target,
  content
) {
  if (
    content &&
    Array.isArray(
      content.blocks
    ) &&
    content.blocks.length
  ) {
    target.push(content);
  }
}


function richTextbookBlock({
  blockId,
  sectionItemId,
  contents,
  contentPrefix,
}) {
  const combined =
    rekeyedContent(
      contents,
      contentPrefix
    );

  if (
    !contentHasMeaningfulText(
      combined
    )
  ) {
    return null;
  }

  const prepared =
    prepareTextbookRichManuscriptBlock(
      {
        blockId,
        type: "paragraph",
        sectionItemId,
        text: "",
      },
      combined
    );

  if (
    !prepared.ok ||
    !prepared.value
  ) {
    throw new Error(
      "The imported Word manuscript could not be converted into a Textbook chapter block safely."
    );
  }

  return prepared.value;
}


function importError(
  error
) {
  return {
    ok: false,
    value: null,
    errors: [
      clean(
        error?.message
      ) ||
        "The Word manuscript could not be prepared for Textbook import.",
    ],
    warnings: [],
  };
}


export function buildTextbookManuscriptImportPlan(
  previewValue,
  {
    idFactory =
      defaultIdFactory,
  } = {}
) {
  try {
    const preview =
      normalizedPreview(
        previewValue
      );

    const sourceSections =
      Array.isArray(
        preview.sections
      )
        ? preview.sections
        : [];

    if (
      sourceSections.length ===
      0
    ) {
      throw new Error(
        "The Word manuscript preview does not contain importable sections."
      );
    }

    let sequence = 0;

    function nextId(
      kind
    ) {
      const value =
        clean(
          idFactory(
            kind,
            sequence
          )
        );

      sequence += 1;

      if (!value) {
        throw new Error(
          "The Textbook import adapter could not create a stable item identifier."
        );
      }

      return value;
    }

    const positiveLevels =
      sourceSections
        .map(
          (section) =>
            Number(
              section
                ?.headingLevel ||
                0
            )
        )
        .filter(
          (level) =>
            Number.isInteger(
              level
            ) &&
            level > 0
        );

    const chapterLevel =
      positiveLevels.length
        ? Math.min(
            ...positiveLevels
          )
        : 0;

    const bookMapItems = [];
    const chapterPlans = [];
    const warnings = [];

    const leadingContents = [];
    const leadingSourceSectionIds =
      [];

    if (
      chapterLevel === 0
    ) {
      const chapterItemId =
        nextId(
          "chapter"
        );

      const blockId =
        nextId(
          "block"
        );

      const content =
        normalizedContent(
          preview.manuscriptContent,
          "the complete Word manuscript"
        );

      const block =
        richTextbookBlock({
          blockId,
          sectionItemId: "",
          contents: [
            content,
          ],
          contentPrefix:
            `${blockId}-content`,
        });

      bookMapItems.push({
        itemId:
          chapterItemId,
        type:
          "chapter",
        title:
          clean(
            preview.title
          ) ||
          "Imported manuscript",
        parentItemId: "",
      });

      chapterPlans.push({
        chapterItemId,
        title:
          clean(
            preview.title
          ) ||
          "Imported manuscript",
        sourceSectionIds:
          sourceSections
            .map(
              (section) =>
                clean(
                  section?.id
                )
            )
            .filter(Boolean),
        blocks:
          block
            ? [block]
            : [],
      });

      return {
        ok: true,

        value: {
          ready: true,

          chapterLevel,

          source: {
            title:
              clean(
                preview.title
              ),
            fileName:
              clean(
                preview.fileName
              ),
            wordCount:
              Number(
                preview.wordCount ||
                0
              ),
            sectionCount:
              sourceSections.length,
          },

          bookMap: {
            items:
              bookMapItems,
          },

          chapterDrafts:
            chapterPlans,

          leadingContent:
            null,

          summary: {
            chapterCount: 1,
            sectionCount: 0,
            wordCount:
              Number(
                preview.wordCount ||
                0
              ),
          },
        },

        errors: [],
        warnings,
      };
    }

    let currentChapter =
      null;

    let currentSection =
      null;

    function ensureChapterDraftBlock(
      plan
    ) {
      const contents =
        plan.rootContents;

      if (
        !contents.length
      ) {
        return;
      }

      const blockId =
        nextId(
          "block"
        );

      const block =
        richTextbookBlock({
          blockId,
          sectionItemId: "",
          contents,
          contentPrefix:
            `${blockId}-content`,
        });

      if (block) {
        plan.blocks.push(
          block
        );
      }

      plan.rootContents = [];
    }

    function ensureSectionDraftBlock(
      plan,
      section
    ) {
      if (
        !section ||
        !section.contents.length
      ) {
        return;
      }

      const blockId =
        nextId(
          "block"
        );

      const block =
        richTextbookBlock({
          blockId,
          sectionItemId:
            section.itemId,
          contents:
            section.contents,
          contentPrefix:
            `${blockId}-content`,
        });

      if (block) {
        plan.blocks.push(
          block
        );
      }

      section.contents = [];
    }

    function finalizeChapter() {
      if (!currentChapter) {
        return;
      }

      ensureSectionDraftBlock(
        currentChapter,
        currentSection
      );

      currentSection = null;

      ensureChapterDraftBlock(
        currentChapter
      );

      delete currentChapter
        .rootContents;

      delete currentChapter
        .sectionPlans;

      chapterPlans.push(
        currentChapter
      );

      currentChapter = null;
    }

    sourceSections.forEach(
      (
        sourceSection,
        sourceIndex
      ) => {
        const sourceId =
          clean(
            sourceSection?.id
          ) ||
          `section-${sourceIndex + 1}`;

        const headingLevel =
          Number(
            sourceSection
              ?.headingLevel ||
              0
          );

        const content =
          normalizedContent(
            sourceSection
              ?.manuscriptContent,
            `Word section ${sourceIndex + 1}`
          );

        if (
          headingLevel === 0 &&
          !currentChapter
        ) {
          pushContent(
            leadingContents,
            content
          );

          leadingSourceSectionIds
            .push(
              sourceId
            );

          return;
        }

        if (
          headingLevel ===
          chapterLevel
        ) {
          finalizeChapter();

          const chapterItemId =
            nextId(
              "chapter"
            );

          const chapterTitle =
            clean(
              sourceSection
                ?.title
            ) ||
            `Chapter ${
              chapterPlans.length + 1
            }`;

          bookMapItems.push({
            itemId:
              chapterItemId,
            type:
              "chapter",
            title:
              chapterTitle,
            parentItemId: "",
          });

          currentChapter = {
            chapterItemId,
            title:
              chapterTitle,
            sourceSectionIds: [
              sourceId,
            ],
            blocks: [],
            rootContents: [],
            sectionPlans: [],
          };

          currentSection =
            null;

          const withoutHeading =
            stripStructuralHeading(
              content,
              `${chapterItemId}-chapter-start`
            );

          pushContent(
            currentChapter
              .rootContents,
            withoutHeading
          );

          return;
        }

        if (!currentChapter) {
          pushContent(
            leadingContents,
            content
          );

          leadingSourceSectionIds
            .push(
              sourceId
            );

          return;
        }

        currentChapter
          .sourceSectionIds
          .push(
            sourceId
          );

        if (
          headingLevel ===
          chapterLevel + 1
        ) {
          /*
           * Chapter-level opening text belongs before the first
           * Textbook Section. Flush it at the structural boundary
           * instead of waiting until chapter finalization.
           */
          ensureChapterDraftBlock(
            currentChapter
          );

          ensureSectionDraftBlock(
            currentChapter,
            currentSection
          );

          const sectionItemId =
            nextId(
              "section"
            );

          const sectionTitle =
            clean(
              sourceSection
                ?.title
            ) ||
            `Section ${
              currentChapter
                .sectionPlans
                .length + 1
            }`;

          bookMapItems.push({
            itemId:
              sectionItemId,
            type:
              "section",
            title:
              sectionTitle,
            parentItemId:
              currentChapter
                .chapterItemId,
          });

          currentSection = {
            itemId:
              sectionItemId,
            title:
              sectionTitle,
            contents: [],
          };

          currentChapter
            .sectionPlans
            .push(
              currentSection
            );

          const withoutHeading =
            stripStructuralHeading(
              content,
              `${sectionItemId}-section-start`
            );

          pushContent(
            currentSection
              .contents,
            withoutHeading
          );

          return;
        }

        /*
         * Deeper Word headings are not promoted into another
         * Book Structure level. Keep them inside the rich text
         * so the writer's hierarchy is preserved without
         * inventing a parallel subsection system.
         */
        if (currentSection) {
          pushContent(
            currentSection
              .contents,
            content
          );
        } else {
          pushContent(
            currentChapter
              .rootContents,
            content
          );
        }
      }
    );

    finalizeChapter();

    if (
      chapterPlans.length ===
      0
    ) {
      throw new Error(
        "The Word manuscript did not contain a usable Textbook chapter heading."
      );
    }

    let leadingContent =
      null;

    if (
      leadingContents.length
    ) {
      const content =
        rekeyedContent(
          leadingContents,
          "textbook-import-leading"
        );

      if (
        contentHasMeaningfulText(
          content
        )
      ) {
        leadingContent = {
          sourceSectionIds:
            leadingSourceSectionIds,

          text:
            plainTextFromManuscriptContent(
              content
            ),

          manuscriptContent:
            content,
        };

        warnings.push(
          "The Word manuscript contains content before the first detected chapter heading. Choose where that opening material belongs before applying the import."
        );
      }
    }

    const sectionCount =
      bookMapItems.filter(
        (item) =>
          item.type ===
            "section"
      ).length;

    return {
      ok: true,

      value: {
        ready:
          !leadingContent,

        chapterLevel,

        source: {
          title:
            clean(
              preview.title
            ),
          fileName:
            clean(
              preview.fileName
            ),
          wordCount:
            Number(
              preview.wordCount ||
              0
            ),
          sectionCount:
            sourceSections.length,
        },

        bookMap: {
          items:
            bookMapItems,
        },

        chapterDrafts:
          chapterPlans,

        leadingContent,

        summary: {
          chapterCount:
            chapterPlans.length,
          sectionCount,
          wordCount:
            Number(
              preview.wordCount ||
              0
            ),
        },
      },

      errors: [],
      warnings,
    };
  } catch (error) {
    return importError(
      error
    );
  }
}
